import { RealmId, RealmIdError } from "@visioni/a3s-realms";

const BASE30_ALPHABET = [
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "J",
  "K",
  "M",
  "N",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "V",
  "W",
  "X",
  "Y",
  "Z",
] as const;
const BASE30_BASE = 30;
const ENCODED_LENGTH = 7;

const VERSION_BITS = 2;
const REALM_BITS = 8;
const UNIQUE_BITS = 22;

const VERSION_SHIFT = REALM_BITS + UNIQUE_BITS;
const REALM_SHIFT = UNIQUE_BITS;

const VERSION_MASK = (1 << VERSION_BITS) - 1;
const UNIQUE_MASK = (1 << UNIQUE_BITS) - 1;

const VERSION_MAX = VERSION_MASK;
const UNIQUE_MAX = UNIQUE_MASK;

const MIN_32BIT = 0;
const MAX_32BIT = 0xffffffff;

declare const TenantIdBrand: unique symbol;

/**
 * A unique identifier for an A3S Tenant.
 *
 * The canonical form is a string: 'T' + seven base-30 characters.
 * Example: "T2277CC8"
 *
 * Internal structure (encoded in the base-30 representation):
 * - Version (2 bits): schema version
 * - Realm (8 bits): identifier of the realm
 * - Unique (22 bits): local uniqueness
 */
export type TenantId = string & { readonly [TenantIdBrand]: "TenantId" };

export class TenantIdError extends Error {
  constructor(
    public readonly kind: "OutOfRange" | "Format",
    message: string,
  ) {
    super(message);
  }
}

/**
 * Encodes a 32-bit number to base-30 using the custom alphabet.
 * Returns a string left-padded to ENCODED_LENGTH characters.
 */
const encodeBase30 = (n: number): string => {
  if (n < 0 || n > MAX_32BIT) {
    throw new TenantIdError("OutOfRange", `Cannot encode ${String(n)} as base-30.`);
  }

  let result = "";
  let remaining = n;

  while (remaining > 0) {
    const digit = remaining % BASE30_BASE;
    // BASE30_BASE === BASE30_ALPHABET.length
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    result = BASE30_ALPHABET[digit]! + result;
    remaining = Math.floor(remaining / BASE30_BASE);
  }

  return result.padStart(ENCODED_LENGTH, BASE30_ALPHABET[0]);
};

/**
 * Decodes a base-30 string to a 32-bit number using the custom alphabet.
 */
const decodeBase30 = (str: string): number => {
  let result = 0;

  for (let i = 0; i < str.length; i++) {
    const char = str.charAt(i);
    const value = BASE30_ALPHABET.findIndex((c) => c === char);

    if (value === -1) {
      throw new TenantIdError("Format", `Invalid base-30 character: ${char}`);
    }

    result = result * BASE30_BASE + value;
  }

  return result;
};

/**
 * Constructs a TenantId from a 32-bit number or throws a TenantIdError.
 * The number is converted to the canonical string form.
 */
const fromNumber = (n: number): TenantId => {
  if (!Number.isInteger(n) || n < MIN_32BIT || n > MAX_32BIT) {
    throw new TenantIdError("OutOfRange", `TenantId ${String(n)} is out of range. Expected 0x00000000 to 0xFFFFFFFF.`);
  }

  const unsigned = n >>> 0;

  // Validate that the realm component is valid
  const realm = (unsigned >>> REALM_SHIFT) & 0xff;
  try {
    RealmId.fromNumber(realm);
  } catch (e) {
    if (e instanceof RealmIdError) {
      throw new TenantIdError("OutOfRange", `Realm ${String(realm)} is out of range. Expected 0x01 to 0xFF.`);
    }
    throw e;
  }

  const encoded = encodeBase30(unsigned);
  return `T${encoded}` as TenantId;
};

/**
 * Constructs a TenantId from its component parts or throws a TenantIdError.
 * The parts are encoded into the canonical string form.
 *
 * @param version - Schema version (0-3)
 * @param realm - Realm identifier (RealmId from @visioni/a3s-realms)
 * @param unique - Local uniqueness value (0-4194303)
 */
const fromParts = (version: number, realm: RealmId, unique: number): TenantId => {
  if (!Number.isInteger(version) || version < 0 || version > VERSION_MAX) {
    throw new TenantIdError(
      "OutOfRange",
      `Version ${String(version)} is out of range. Expected 0 to ${String(VERSION_MAX)}.`,
    );
  }

  if (!Number.isInteger(unique) || unique < 0 || unique > UNIQUE_MAX) {
    throw new TenantIdError(
      "OutOfRange",
      `Unique ${String(unique)} is out of range. Expected 0 to ${String(UNIQUE_MAX)}.`,
    );
  }

  const id = ((version << VERSION_SHIFT) | (realm << REALM_SHIFT) | unique) >>> 0;
  return fromNumber(id);
};

/**
 * Returns the string representation of the TenantId.
 * Since TenantId is already in canonical string form, this simply returns the value.
 */
const toString = (id: TenantId): string => {
  return id;
};

/**
 * Parses a TenantId from canonical encoding or throws a TenantIdError.
 * (Accepts only `/^T[23456789ABCDEFGHJKMNPQRSTVWXYZ]{7}$/`.)
 */
const fromString = (str: string): TenantId => {
  if (str.length !== ENCODED_LENGTH + 1 || str.charAt(0) !== "T") {
    throw new TenantIdError(
      "Format",
      `String ${str} is not a valid TenantId. Expected format: T + ${String(ENCODED_LENGTH)} base-30 characters.`,
    );
  }

  const encoded = str.slice(1);
  const n = decodeBase30(encoded);

  if (n > MAX_32BIT) {
    throw new TenantIdError("OutOfRange", `Decoded value ${String(n)} exceeds 32-bit range.`);
  }

  // Validate that the realm component is valid
  const realm = (n >>> REALM_SHIFT) & 0xff;
  try {
    RealmId.fromNumber(realm);
  } catch (e) {
    if (e instanceof RealmIdError) {
      throw new TenantIdError("OutOfRange", `Realm ${String(realm)} is out of range. Expected 0x01 to 0xFF.`);
    }
    throw e;
  }

  return str as TenantId;
};

/**
 * Extracts the component parts from a TenantId.
 * Returns the version, realm, and unique components.
 */
const toParts = (id: TenantId): { version: number; realm: RealmId; unique: number } => {
  const str = toString(id);
  const encoded = str.slice(1);
  const n = decodeBase30(encoded);

  const version = (n >>> VERSION_SHIFT) & VERSION_MASK;
  const realm = RealmId.fromNumber((n >>> REALM_SHIFT) & 0xff);
  const unique = n & UNIQUE_MASK;

  return { version, realm, unique };
};

export const TenantId = {
  fromNumber,
  fromParts,
  toParts,
  toString,
  fromString,
} as const;
